import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LogViewer } from "@/components/dev/LogViewer";
import { YDMWordmark } from "@/components/brand";
import { Settings as SettingsIcon, HelpCircle, FileCode, Shield, Bot, LayoutDashboard, Gauge, Layers, GalleryHorizontal, Target, Calculator } from "lucide-react";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DemoDataProvider } from "@/contexts/DemoDataContext";
import { ChannelFilterProvider } from "@/contexts/ChannelFilterContext";
import Help from "./pages/Help";
import ReportLibrary from "./pages/ReportLibrary";
import LookerDashboard from "./pages/LookerDashboard";
import ExecutiveOverview from "./pages/ExecutiveOverview";
import ChannelDeepDive from "./pages/ChannelDeepDive";
import CreativePerformance from "./pages/CreativePerformance";
import AudienceTargeting from "./pages/AudienceTargeting";
import BudgetMmm from "./pages/BudgetMmm";
import { isLookerMenuEnabled } from "./config/lookerConfig";
import { START_PAGE } from "./config/startPage";
import AcceptInvite from "./pages/AcceptInvite";
import AdminUsers from "./pages/admin/Users";
import AdminWorkspaces from "./pages/admin/Workspaces";
import AdminSyncSchedules from "./pages/admin/SyncSchedules";
import AdminSyncHistory from "./pages/admin/SyncHistory";
import AdminBigQuerySync from "./pages/admin/BigQuerySync";
import AdminActivityLog from "./pages/admin/ActivityLog";
import AdminAiHub from "./pages/admin/AiHub";
import AdminLookerTemplates from "./pages/admin/LookerTemplates";
import AdminLookerTemplateBuilder from "./pages/admin/LookerTemplateBuilder";
import AdminMmmModels from "./pages/admin/MmmModels";
import Signin from "./pages/auth/Signin";
import Signup from "./pages/auth/Signup";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import AuthCallback from "./pages/auth/AuthCallback";
import Settings from "./pages/Settings";
import OAuthCallback from "./pages/OAuthCallback";
import { useIsPlatformMaster } from "./hooks/useIsPlatformMaster";

// Inner app component that uses auth context
const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading, signOut } = useAuth();
  const [isLogViewerOpen, setIsLogViewerOpen] = useState(false);
  const isAuthPage = location.pathname.startsWith("/auth");
  const isOAuthCallback = location.pathname.startsWith("/settings/integrations/callback");
  // Rendered outside the session gate below. An invitee is by definition someone
  // who may not have an account yet, and the gate's <Navigate> keeps no search
  // string — so the token in the link was discarded before the page ever ran.
  const isInviteAccept = location.pathname === "/invite/accept";

  // Keyboard shortcut for log viewer (Ctrl+Shift+L)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.shiftKey && event.key === "L") {
      event.preventDefault();
      setIsLogViewerOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleLogout = useCallback(async () => {
    await signOut();
    navigate("/auth/signin");
  }, [signOut, navigate]);

  const { isMaster } = useIsPlatformMaster(!!session);

  const navigation = [
    // Native D1 on Supabase (spec 2026-10-14), labelled "Overview". The first row
    // and the start page since 2026-10-15 (owner): it took the Dashboard's place,
    // and the Dashboard ("/") left the menu — see START_PAGE.
    { name: t("nav.executiveOverview"), href: "/overview", icon: Gauge },
    // Native D2 on Supabase (spec 2026-10-14, R23): directly below Overview.
    { name: t("nav.channelDeepDive"), href: "/channels", icon: Layers },
    // Native D3 on Supabase (spec 2026-10-15, R23): directly below Channel Deep-dive, for every role.
    { name: t("nav.creativePerformance"), href: "/creatives", icon: GalleryHorizontal },
    // Native D4 on Supabase (spec 2026-10-16, R15): directly below Creative Performance, for every role.
    { name: t("nav.audienceTargeting"), href: "/audience", icon: Target },
    // Native D5 on Meridian (spec 2026-10-28, R28): directly below Audience / Targeting, above Looker, for every role.
    { name: t("nav.budgetMmm"), href: "/budget", icon: Calculator },
    // Hidden until Looker is connected (VITE_LOOKER_ENABLED); the route stays.
    // Below the native pages since 2026-10-15 (owner); right under the Dashboard before.
    ...(isLookerMenuEnabled() ? [{ name: t("nav.lookerDashboard"), href: "/looker", icon: LayoutDashboard }] : []),
    // Media Intelligence (/campaigns) and Insights (/insights) left the menu on 2026-10-16 (owner), and MMM Analysis
    // (/mmm) on 2026-10-30, when Budget & MMM replaced it (D5 R22); see the routes.
    { name: t("nav.reports"), href: "/reports", icon: FileCode },
    { name: t("nav.settings"), href: "/settings", icon: SettingsIcon },
    { name: t("nav.help"), href: "/help", icon: HelpCircle },
    // The only conditional item in this array. Hiding it is a courtesy, not a
    // control: /admin/users renders for anyone who types it, and every RPC it
    // calls checks is_platform_master() in Postgres.
    ...(isMaster
      ? [
          {
            name: "Super Admin",
            href: "/admin",
            icon: Shield,
            children: [
              { name: "User", href: "/admin/users", icon: Shield },
              { name: "Workspace", href: "/admin/workspaces", icon: Shield },
              { name: "Sync schedules", href: "/admin/sync-schedules", icon: Shield },
              { name: "BigQuery sync", href: "/admin/bigquery-sync", icon: Shield },
              { name: "Activity log", href: "/admin/activity", icon: Shield },
              // Hidden with /looker until Looker is connected; the route stays.
              ...(isLookerMenuEnabled() ? [{ name: "Looker templates", href: "/admin/looker-templates", icon: Shield }] : []),
              // Usage, credits, models and skills are four views of one
              // subsystem and were four of this menu's eight rows; they live
              // behind tabs at /admin/ai now.
              { name: "AI", href: "/admin/ai", icon: Bot },
              // D5 (plan R20): download the MMM training data and upload Meridian versions, per workspace.
              { name: t("nav.budgetAdmin"), href: "/admin/mmm-models", icon: Shield },
            ],
          },
        ]
      : []),
  ];

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Auth routes (no layout or workspace wrapper)
  if (isAuthPage) {
    // Already authenticated? Don't sit on signin/signup — go to the app. This
    // also covers the brief window right after login where the redirect fires
    // before the auth session settles.
    //
    // EXCEPT the reset page. Following a recovery link MINTS a session — that
    // is how the link authenticates you — so this redirect would fire on
    // arrival and drop the user into the app with a password they still cannot
    // remember, having never seen the form. The one route whose whole purpose
    // is to be reached WITH a session has to be exempt from the rule that a
    // session means you belong elsewhere.
    if (session && location.pathname !== "/auth/reset-password") {
      return <Navigate to={START_PAGE} replace />;
    }
    return (
      <Routes>
        <Route path="/auth/signin" element={<Signin />} />
        <Route path="/auth/signup" element={<Signup />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        {/* Must precede the catch-all: /auth/* redirects, and a redirect drops
            the ?code= that the confirmation link carries. */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/*" element={<Navigate to="/auth/signin" replace />} />
      </Routes>
    );
  }

  // OAuth callback route (no layout - runs in popup window)
  if (isOAuthCallback) {
    return (
      <Routes>
        <Route path="/settings/integrations/callback" element={<OAuthCallback />} />
      </Routes>
    );
  }

  // The invite page decides for itself what a missing session means, so that a
  // signed-out invitee is told to sign in instead of being bounced to a form that
  // says nothing and no longer holds their token.
  if (isInviteAccept) {
    return (
      <Routes>
        <Route path="/invite/accept" element={<AcceptInvite />} />
      </Routes>
    );
  }

  // Redirect to signin if not authenticated
  if (!session) {
    return <Navigate to="/auth/signin" replace />;
  }

  // App routes (with layout and workspace context)
  return (
    <WorkspaceProvider>
      <DemoDataProvider>
        <ChannelFilterProvider>
          <Layout
            navigation={navigation}
            title="Media Intelligence"
            session={session}
            onLogout={handleLogout}
            logo={<YDMWordmark variant="full" bg="dark" size="md" />}
          >
            {/* Inside Layout so a page crash leaves the sidebar usable; resetKey
                clears it on navigation, without which the nav would look alive
                but do nothing. location.key (not pathname) also covers
                back/forward and re-clicking the current route. */}
            <ErrorBoundary category="Route" resetKey={location.key}>
              <Routes>
                {/* The Dashboard (pages/Home) is hidden since 2026-10-15 (owner):
                    "/" opens the start page instead. The page itself is kept —
                    import it and route it here again to bring it back, and add
                    `performance` back to SHOWN_MODES in src/pages/admin/AgentProfiles.tsx:
                    without its Skills card, its chat runs on any stored agent_profiles
                    row with no editor and no reset. */}
                <Route path="/" element={<Navigate to={START_PAGE} replace />} />
                {/* Media Intelligence (/campaigns) and Insights (/insights) are hidden since 2026-10-16
                    (owner), the same way: no menu row and no route, so both URLs, and an old
                    /insights/... bookmark, fall through to the catch-all and open the start page. The
                    pages are kept — import them, put their menu rows back and route them here again
                    to bring them back, and add their chat modes back to SHOWN_MODES in
                    src/pages/admin/AgentProfiles.tsx (`performance` for /campaigns, `insights` for
                    /insights), for the same reason as the Dashboard above. */}
                {/* MMM Analysis (pages/MMMDetail) is hidden since 2026-10-30 (D5 R22): /budget replaced it, so an old
                    /mmm link opens /budget. Only /mmm itself redirects: there is no /mmm/* route, and none should
                    point at a path that redirects again. The page is kept — import it, put its menu row back and
                    route it here again to bring it back, and add `mmm` back to SHOWN_MODES in
                    src/pages/admin/AgentProfiles.tsx, for the same reason as the Dashboard above. */}
                <Route path="/mmm" element={<Navigate to="/budget" replace />} />
                <Route path="/reports" element={<ReportLibrary />} />
                <Route path="/help" element={<Help />} />
                <Route path="/looker" element={<LookerDashboard />} />
                <Route path="/overview" element={<ExecutiveOverview />} />
                {/* One element for both: /channels picks a channel and an unknown one comes back to it (R6). */}
                <Route path="/channels" element={<ChannelDeepDive />} />
                <Route path="/channels/:platform" element={<ChannelDeepDive />} />
                <Route path="/creatives" element={<CreativePerformance />} />
                <Route path="/audience" element={<AudienceTargeting />} />
                <Route path="/budget" element={<BudgetMmm />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/:tab" element={<Settings />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
                <Route path="/admin/activity" element={<AdminActivityLog />} />
                <Route path="/admin/sync-schedules" element={<AdminSyncSchedules />} />
                <Route path="/admin/sync-history" element={<AdminSyncHistory />} />
                <Route path="/admin/bigquery-sync" element={<AdminBigQuerySync />} />
                <Route path="/admin/mmm-models" element={<AdminMmmModels />} />
                <Route path="/admin/looker-templates" element={<AdminLookerTemplates />} />
                <Route path="/admin/looker-templates/new" element={<AdminLookerTemplateBuilder />} />
                <Route path="/admin/looker-templates/:id/edit" element={<AdminLookerTemplateBuilder />} />
                <Route path="/admin/ai" element={<AdminAiHub />} />
                <Route path="/admin/ai/:tab" element={<AdminAiHub />} />
                {/* The routes these four used to own. Kept as redirects because a
                    master may have bookmarked one, and because an error message
                    the user can see names one of them. */}
                <Route path="/admin/ai-usage" element={<Navigate to="/admin/ai/usage" replace />} />
                <Route path="/admin/credits" element={<Navigate to="/admin/ai/credits" replace />} />
                <Route path="/admin/ai-models" element={<Navigate to="/admin/ai/models" replace />} />
                <Route path="/admin/agent-profiles" element={<Navigate to="/admin/ai/skills" replace />} />
                <Route path="*" element={<Navigate to={START_PAGE} replace />} />
              </Routes>
            </ErrorBoundary>
          </Layout>
          <LogViewer isOpen={isLogViewerOpen} onClose={() => setIsLogViewerOpen(false)} />
        </ChannelFilterProvider>
      </DemoDataProvider>
    </WorkspaceProvider>
  );
};

// Main App component with AuthProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
